import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CreditCard, ExternalLink } from 'lucide-react';
import {
  useDisconnectStripe,
  useStartStripeConnect,
  useStripeConnectStatus,
} from '@repo/queries';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert } from '@/components/ui/alert';
import { useToast } from '@/components/ui/use-toast';
import { describeAppError } from '@/features/common/describeAppError';
import { useActiveRole } from '@/features/auth/useActiveRole';

export function PaymentsSettingsScreen() {
  const { data: status, isPending: loading, refetch } = useStripeConnectStatus();
  const startConnect = useStartStripeConnect();
  const disconnect = useDisconnectStripe();
  const { toast } = useToast();
  const { canEditOrg } = useActiveRole();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const stripe = searchParams.get('stripe');
    if (stripe === 'connected') {
      void refetch();
      toast({ title: 'Stripe connected', variant: 'success' });
      searchParams.delete('stripe');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, refetch, toast]);

  async function onConnect() {
    try {
      const { url } = await startConnect.mutateAsync();
      window.location.href = url;
    } catch (err) {
      const desc = describeAppError(err);
      toast({ title: desc.title, description: desc.message, variant: 'danger' });
    }
  }

  async function onDisconnect() {
    try {
      await disconnect.mutateAsync();
      toast({ title: 'Stripe disconnected', variant: 'success' });
    } catch (err) {
      const desc = describeAppError(err);
      toast({ title: desc.title, description: desc.message, variant: 'danger' });
    }
  }

  if (loading) {
    return (
      <Page>
        <Skeleton className="h-10 w-72" />
        <Skeleton className="mt-6 h-48 rounded-xl" />
      </Page>
    );
  }

  const ready = status?.connected && status.chargesEnabled;

  return (
    <Page>
      <PageHeader
        title="Payments"
        description="Connect Stripe so clients can pay invoices online from the public link."
      />

      <Card className="max-w-2xl">
        <CardHeader className="flex flex-row items-center gap-3 space-y-0">
          <div className="flex size-10 items-center justify-center rounded-lg bg-secondary">
            <CreditCard className="size-5 text-foreground" />
          </div>
          <div>
            <CardTitle className="text-base">Stripe</CardTitle>
            <p className="text-sm text-muted-foreground">
              Card checkout on your connected account — funds go directly to you.
            </p>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {!canEditOrg ? (
            <Alert variant="info">
              Only workspace owners and admins can connect payment processors.
            </Alert>
          ) : null}

          {ready ? (
            <Alert variant="success">
              Stripe is connected and ready. Enable &quot;Accept card payment online&quot; on each
              invoice you want clients to pay from the link.
            </Alert>
          ) : status?.connected ? (
            <Alert variant="warning">
              Stripe onboarding is incomplete. Finish setup to accept online payments.
            </Alert>
          ) : (
            <Alert variant="info">
              Connect Stripe once per business. Then toggle online checkout on individual invoices.
            </Alert>
          )}

          <div className="flex flex-wrap gap-2">
            {canEditOrg ? (
              ready ? (
                <Button variant="outline" onClick={() => void onDisconnect()} disabled={disconnect.isPending}>
                  Disconnect
                </Button>
              ) : (
                <Button onClick={() => void onConnect()} disabled={startConnect.isPending}>
                  <ExternalLink className="size-4" />
                  {status?.connected ? 'Continue Stripe setup' : 'Connect Stripe'}
                </Button>
              )
            ) : null}
          </div>
        </CardContent>
      </Card>
    </Page>
  );
}
